import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HeroSection from "../../components/common/HeroSection";
import PageShell from "../../components/common/PageShell";
import ProductFilters from "../../components/products/ProductFilters";
import ProductGrid from "../../components/products/ProductGrid";
import ProductSearch from "../../components/products/ProductSearch";
import ProductsStateMessage from "../../components/products/ProductsStateMessage";
import { ProductGridSkeleton } from "../../components/products/ProductSkeleton";
import { PAGE_HEROES } from "../../constants/pageHeroes";
import { isWordPressConfigured } from "../../constants/wordpress";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useProductCategories } from "../../hooks/useProductCategories";
import { useProducts } from "../../hooks/useProducts";
import { SECTION_CONTAINER } from "../../utils/layout";

function Products() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);
  const { categories } = useProductCategories();

  const categorySlug = searchParams.get("category");
  const activeCategory = useMemo(
    () => categories.find((category) => category.slug === categorySlug) || null,
    [categories, categorySlug],
  );

  const { products, totalPages, loading, error, refetch } = useProducts({
    page,
    categoryId: activeCategory?.id || null,
    search: debouncedSearch,
  });

  useEffect(() => {
    setPage(1);
  }, [categorySlug, debouncedSearch]);

  const handleCategoryChange = (category) => {
    const next = new URLSearchParams(searchParams);
    if (category?.slug) {
      next.set("category", category.slug);
    } else {
      next.delete("category");
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <PageShell>
      <HeroSection {...PAGE_HEROES.products} variant="page" />
      <div className="caf-section caf-section--light">
        <section
          className="caf-section__block caf-section--elevated caf-section--elevated-tight caf-page-section"
          aria-labelledby="products-catalog-title"
        >
          <h2 id="products-catalog-title" className="sr-only">
            {t("Products")}
          </h2>
          <div className={`${SECTION_CONTAINER} caf-page-section__body pb-8`}>
            {!isWordPressConfigured() ? (
              <ProductsStateMessage
                title={t("Products unavailable")}
                message={t(
                  "WordPress API credentials are missing. Add REACT_APP_WC_CONSUMER_KEY and REACT_APP_WC_CONSUMER_SECRET to your environment file.",
                )}
              />
            ) : (
              <>
                <ProductSearch value={searchInput} onChange={setSearchInput} />
                <ProductFilters
                  categories={categories}
                  activeCategorySlug={categorySlug}
                  onChange={handleCategoryChange}
                />

                {loading ? <ProductGridSkeleton count={6} /> : null}

                {!loading && error ? (
                  <ProductsStateMessage
                    title={t("Unable to load products")}
                    message={error}
                    onRetry={refetch}
                  />
                ) : null}

                {!loading && !error && !products.length ? (
                  <ProductsStateMessage
                    title={t("No products found")}
                    message={t("There are no published products in this category yet.")}
                  />
                ) : null}

                {!loading && !error && products.length ? (
                  <>
                    <ProductGrid products={products} showAddToCart />
                    {totalPages > 1 ? (
                      <div className="caf-products-pagination">
                        <button
                          type="button"
                          className="caf-btn caf-btn--outline px-4 py-2 text-sm"
                          disabled={page <= 1}
                          onClick={() => setPage((current) => current - 1)}
                        >
                          {t("Previous")}
                        </button>
                        <span className="caf-products-pagination__info">
                          {t("Page")} {page} / {totalPages}
                        </span>
                        <button
                          type="button"
                          className="caf-btn caf-btn--outline px-4 py-2 text-sm"
                          disabled={page >= totalPages}
                          onClick={() => setPage((current) => current + 1)}
                        >
                          {t("Next")}
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </>
            )}
          </div>
        </section>
      </div>
    </PageShell>
  );
}

export default Products;
